import React, { useRef, useState } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Icon from 'dpl/common/components/Icon';
import SmartImage from 'dpl/components/SmartImage';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';

import certifiedBreeder1Jpg from 'dpl/assets/reviews/certified_breeder_1.jpg';
import certifiedBreeder2Jpg from 'dpl/assets/reviews/certified_breeder_2.jpg';
import certifiedBreeder3Jpg from 'dpl/assets/reviews/certified_breeder_3.jpg';
import certifiedBreeder4Jpg from 'dpl/assets/reviews/certified_breeder_4.jpg';
import certifiedBreeder5Jpg from 'dpl/assets/reviews/certified_breeder_5.jpg';

const certifiedBreeders = [
  {
    id: 129279,
    display_name: 'Alisa O.',
    program_name: 'FriendlyPals',
    testimonial:
      'Good Dog not only vets breeders but also provides resources that breeders can use to grow, learn and share. The general population is catching on...the people who inquire with me on Good Dog are far more ready and far more educated.',
    image_url: certifiedBreeder1Jpg,
    breeder_profile_url:
      'https://www.gooddog.com/breeders/friendlypals-pennsylvania'
  },
  {
    id: 37421,
    display_name: 'Ronnette Davis',
    program_name: 'Von Baron Schnauzers',
    testimonial:
      'I owe Good Dog a great deal. I found them and I never looked back. The amazing support both with other breeder enthusiasts and the vast knowledge on their website is endless. I keep growing as a breeder every day because of the new information that is provided to me.',
    image_url: certifiedBreeder2Jpg,
    breeder_profile_url:
      'https://www.gooddog.com/breeders/von-baron-schnauzers-texas'
  },
  {
    id: 140611,
    display_name: 'Suelin Ammons',
    program_name: 'Golden Heart Creams',
    testimonial:
      'I love this company!  Any time I have needed help they reach out immediately and a very helpful representative will answer all my questions and more! I sold my puppies through them in less than a week. I highly recommend them.',
    image_url: certifiedBreeder3Jpg,
    breeder_profile_url:
      'https://www.gooddog.com/breeders/golden-heart-creams-north-carolina'
  },
  {
    id: 12171,
    display_name: "D'Arcy Downs-Vollbracht",
    program_name: 'Fortune Farms',
    testimonial:
      "I love being a Good Dog member! They support breeders and prioritize canine health. Their education, discounts, scam protection, and meaningful partnerships with breed clubs continue to impress me. There's something for every breeder, regardless of breed or experience level.",
    image_url: certifiedBreeder4Jpg,
    breeder_profile_url:
      'https://www.gooddog.com/breeders/fortune-farms-arizona'
  },
  {
    id: 9599,
    display_name: 'Alana & Kelly Curry',
    program_name: 'Puppies Unleashed',
    testimonial:
      'Becoming a member of Good Dog was the best thing to happen to our program! Even as experienced breeders, the education, legal resources and litter management system Good Dog provides, give us the support we need for the future.',
    image_url: certifiedBreeder5Jpg,
    breeder_profile_url:
      'https://www.gooddog.com/breeders/puppies-unleashed-texas'
  }
];

const IS_GT_SM = isGreaterThanBreakpoint('sm');
const IS_GT_MD = isGreaterThanBreakpoint('md');
const desktopImageSize = isGreaterThanBreakpoint('lg') ? '433px' : '310px';

function ModulePagination({
  className,
  onNextClick,
  onPrevClick,
  pageCount,
  currentPage
}) {
  if (pageCount <= 1) return null;

  return (
    <div
      className={classnames(
        'CertifiedBreedersModule__pagination flex flex-column gap-8 mw-100 mw-30-md',
        className
      )}
    >
      <div className="relative w-100" style={{ height: 2 }}>
        <span className="absolute bg-stone-700 db o-20 h-100 w-100" />
        <span
          className="absolute bg-stone-700 db h-100 transition"
          style={{
            width: `${(100 / pageCount) * currentPage}%`
          }}
        />
      </div>
      <div className="flex gap-24">
        <button
          className="pv2 ph2 br-100"
          type="button"
          aria-label="Previous story"
          onClick={onPrevClick}
        >
          <Icon name="fetch-arrow-left" height="24px" width="24px" />
        </button>
        <button
          className="pv2 ph2 br-100"
          type="button"
          aria-label="Next story"
          onClick={onNextClick}
        >
          <Icon name="fetch-arrow-right" height="24px" width="24px" />
        </button>
      </div>
    </div>
  );
}

ModulePagination.propTypes = {
  className: PropTypes.string,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired
};

ModulePagination.defaultProps = {
  className: null
};

export default function CertifiedBreedersModule() {
  const splideRef = useRef(null);
  const [currentSlideNumber, setCurrentSlideNumber] = useState(1);

  return (
    <div className="CertifiedBreedersModule container-xxl mt20">
      <p className="mb6 mb10-md font-24 md:font-32 font-serif">
        Hear from our breeders
      </p>
      <div className="bg-sky br4 pv4 ph4 pv10-md ph10-md">
        <Splide
          ref={splideRef}
          onActive={({ index }) => setCurrentSlideNumber(index + 1)}
          options={{
            perPage: 1,
            gap: 72,
            autoplay: true,
            rewind: true,
            arrows: false
          }}
        >
          {certifiedBreeders.map(breeder => {
            return (
              <SplideSlide key={breeder.id}>
                <div
                  className={classnames(
                    'flex justify-between flex-column flex-md-row',
                    { 'h-100': !IS_GT_SM }
                  )}
                >
                  <div className={classnames('pr4', { 'w-50': IS_GT_SM })}>
                    <p className="stone-700 font-serif font-20 md:font-24 mb1">
                      {breeder.display_name}
                    </p>
                    <p className="stone-700 o-80 font-14 mb4 mb8-md">
                      Verified breeder of{' '}
                      <a
                        className="underline"
                        href={breeder.breeder_profile_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {breeder.program_name}
                      </a>
                    </p>
                    <p className="stone-700 font-16 md:font-20 font-serif mb6 mb0-md">
                      {breeder.testimonial}
                    </p>
                  </div>
                  <SmartImage
                    src={breeder.image_url}
                    alt={breeder.program_name}
                    className="br4"
                    width={IS_GT_MD && desktopImageSize}
                    height={IS_GT_MD && desktopImageSize}
                  />
                </div>
              </SplideSlide>
            );
          })}
        </Splide>
        <ModulePagination
          className={classnames({ 'w-50': IS_GT_MD })}
          pageCount={certifiedBreeders.length}
          currentPage={currentSlideNumber}
          onPrevClick={() => splideRef.current.go('<')}
          onNextClick={() => splideRef.current.go('>')}
        />
      </div>
    </div>
  );
}
